import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ICellRendererParams } from '@ag-grid-community/core'
import { DataTable, DataTablePagination } from 'components/common'
import { useQuery } from 'hooks'
import useModal from 'hooks/useModal'
import { fetchFtpProfiles } from 'utils/api/ftpProfileApi'
import { getColumns } from './utils'
import ConfirmDeleteFtpProfileModal from './confirmDeleteFtpProfileModal'

type FtpProfilesTableProps = {
  isEditor: boolean
}

const FtpProfilesTable = ({ isEditor }: FtpProfilesTableProps) => {
  const [ftpProfilesData, setFtpProfilesData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)

  // preserve the selected ftp profile params while open modal for deletion
  const [gridParams, setGridParams] = useState<ICellRendererParams>()

  const { search } = useLocation()
  const { getAllQueries } = useQuery()

  const { closeModal, isOpen, openModal } = useModal()

  const getFtpProfiles = async () => {
    setLoading(true)
    const { limit, search: SearchTerm, page } = getAllQueries()
    try {
      const { data } = await fetchFtpProfiles({
        limit: limit || 10,
        page: page || 1,
        search: SearchTerm,
      })
      setFtpProfilesData({ ...data })
    } catch (error) {
      setFtpProfilesData({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getFtpProfiles()
  }, [search])

  const getFtpProfileToDelete = (params: ICellRendererParams) => {
    if (!isEditor) return
    setGridParams(params)
    openModal()
  }

  const columns = useMemo(
    () => getColumns(isEditor, getFtpProfileToDelete),
    [isEditor],
  )

  return (
    <>
      <DataTable
        columns={columns}
        rows={loading ? null : ftpProfilesData?.docs}
        identifier="_id"
      />

      {/* Confirm Delete Modal */}
      {isEditor && (
        <ConfirmDeleteFtpProfileModal
          params={gridParams}
          closeModal={closeModal}
          isOpen={isOpen}
        />
      )}

      {/* Pagination */}
      <DataTablePagination
        currentPage={ftpProfilesData?.page}
        totalPages={ftpProfilesData?.totalPages}
        hasNextPage={ftpProfilesData?.hasNextPage}
        hasPrevPage={ftpProfilesData?.hasPrevPage}
        limit={ftpProfilesData?.limit}
        nextPage={ftpProfilesData?.nextPage}
        prevPage={ftpProfilesData?.prevPage}
      />
    </>
  )
}

export default FtpProfilesTable
